import React, { useMemo, useCallback, useState, useRef, useLayoutEffect } from 'react';
import {
  MainContent,
  TopRow,
  AvatarWrapper,
  IdentityBlock,
  RoleChip,
  CardContentWrapper,
  NextBestActionBlock,
  NextBestActionLabel,
  NextBestActionLabelText,
  NextBestActionContentRow,
  NextBestActionTruncated,
  ViewMoreLink,
  ActivitySectionRow,
  ActivitySectionWrapper,
  ActivityRow,
  ActivityItem,
  ActivitySeparator,
  IdentitySeparator,
  RightBlock,
  StatusRow,
  CardContentRow,
  CardHighlightWrapper,
} from './ContactCard.styled';
import { Card, Typography, Tag, Icon, ButtonMenu } from '@ann/web-components';
import NextBestActionModal from '../NextBestActionModal';
import CopyableContactItem from './CopyableContactItem';

const AVATAR_COLORS = [
  '#e9f1ff', // light blue (Border/Primary/Subtle)
  '#f3e9fd', // light purple (Border/Purple/Subtle)
  '#e8f5e9', // light green
  '#fce4ec', // light pink
  '#fff3e0', // light orange
];

const getInitials = (name) => {
  if (!name || typeof name !== 'string') return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const getAvatarColor = (name) => {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  const str = String(name);
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
};

const TAG_VARIANT_TO_COLOR = {
  default: 'neutral',
  gray: 'neutral',
  blue: 'informative',
  orange: 'alert',
  red: 'error',
};

const ROLE_ICON = {
  customer: 'person',
  employee: 'adminPanelSettings',
  company: 'domain',
};

const getHighestSeverity = (tags) => {
  for (const tag of tags) {
    if (tag.variant === 'red') return 'error';
  }
  for (const tag of tags) {
    if (tag.variant === 'orange') return 'alert';
  }
  return null;
};

const ContactCard = ({
  contact,
  role,
  company,
  nextBestAction,
  activitySummary,
  statusTags = [],
  isArchived = false,
  onPreview,
  onNavigate,
  onEmail,
  onCall,
  onEdit,
  onArchive,
}) => {
  const { legalName, partyUuid } = contact;

  const handleMenuChange = useCallback(
    (value) => {
      switch (value) {
        case 'view_details':
          onNavigate?.(partyUuid);
          break;
        case 'edit':
          onEdit?.(contact);
          break;
        case 'archive':
          onArchive?.(contact);
          break;
        default:
          break;
      }
    },
    [contact, onNavigate, onEdit, onArchive, partyUuid]
  );

  const initials = useMemo(() => getInitials(legalName), [legalName]);
  const avatarColor = useMemo(() => getAvatarColor(legalName), [legalName]);
  const cardSeverity = useMemo(() => getHighestSeverity(statusTags), [statusTags]);

  const roleKey = role?.toLowerCase?.() ?? 'customer';
  const roleIcon = ROLE_ICON[roleKey] ?? 'person';

  const [isNextBestActionModalOpen, setIsNextBestActionModalOpen] = useState(false);
  const [hasEllipsis, setHasEllipsis] = useState(false);
  const truncatedTextRef = useRef(null);

  useLayoutEffect(() => {
    const el = truncatedTextRef.current;
    if (!el || !nextBestAction) return;

    let mounted = true;

    const checkOverflow = () => {
      const contentEl = el.firstElementChild;
      if (!contentEl || !mounted) return;
      setHasEllipsis(contentEl.scrollWidth > contentEl.clientWidth);
    };

    checkOverflow();
    const observer = new ResizeObserver(checkOverflow);
    observer.observe(el);

    return () => {
      mounted = false;
      observer.disconnect();
    };
  }, [nextBestAction]);

  const handleViewMoreClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsNextBestActionModalOpen(true);
  }, []);

  const handleCloseNextBestActionModal = useCallback(() => {
    setIsNextBestActionModalOpen(false);
  }, []);

  const handleCardClick = useCallback(
    (e) => {
      if (onNavigate && !e.target.closest('button, [role="button"], [role="menu"], [role="menuitem"], a')) {
        onNavigate(partyUuid);
      }
    },
    [onNavigate, partyUuid]
  );

  return (
    <CardHighlightWrapper $severity={cardSeverity} data-testid="contact-card" data-contact-id={partyUuid}>
      <Card onClick={handleCardClick} size="small" gap="4x">
        <CardContentWrapper>
        <CardContentRow>
          <MainContent>
            <TopRow>
              <AvatarWrapper $color={avatarColor}>{initials}</AvatarWrapper>
              <IdentityBlock>
                <Typography category="subtitle" size="medium" weight="bold" color="title">
                  {legalName || '—'}
                </Typography>
                <IdentitySeparator>·</IdentitySeparator>
                <RoleChip>
                  <Icon name={roleIcon} width="14px" height="14px" />
                  <Typography category="body" size="small" weight="medium" color="subtitle">
                    {role}
                  </Typography>
                </RoleChip>
                {company && (
                  <>
                    {/* <IdentitySeparator>·</IdentitySeparator> */}
                    {/* <CompanyText>
                      <Icon name="work" width="14px" height="14px" />
                      <Typography category="body" size="small" weight="medium" color="subtitle">
                        {company}
                      </Typography>
                    </CompanyText> */}
                  </>
                )}
              </IdentityBlock>
            </TopRow>
          </MainContent>

          <RightBlock>
            <StatusRow>
              {isArchived && (
                <Tag text="Archived" color="neutral" size="small" />
              )}
              {statusTags.map((tag) => (
                <Tag
                  key={tag.label}
                  text={tag.label}
                  color={TAG_VARIANT_TO_COLOR[tag.variant] ?? 'neutral'}
                  size="small"
                />
              ))}
              <ButtonMenu
                customIcon="dotsMenu"
                size="small"
                type="tertiary"
                pill
                onChange={handleMenuChange}
              >
                <ButtonMenu.Menu icon={true}>
                  <ButtonMenu.Item label="View details" value="view_details" icon="short_text">
                    View details
                  </ButtonMenu.Item>
                  <ButtonMenu.Item label="Edit" value="edit" icon="edit" disabled={true}>
                    Edit
                  </ButtonMenu.Item>
                  <ButtonMenu.Item
                    label={isArchived ? 'Unarchive' : 'Archive'}
                    value="archive"
                    icon={isArchived ? 'inbox' : 'delete'}
                  >
                    {isArchived ? 'Unarchive' : 'Archive'}
                  </ButtonMenu.Item>
                </ButtonMenu.Menu>
              </ButtonMenu>
            </StatusRow>
          </RightBlock>
        </CardContentRow>

        {nextBestAction && (
          <NextBestActionBlock>
            <NextBestActionLabel>
              <Icon color="purple" name="aiChat" size="small" />
              <NextBestActionLabelText>
                <Typography category="body" size="small" weight="medium" color="primary">
                  Next best action
                </Typography>
              </NextBestActionLabelText>
            </NextBestActionLabel>
            <NextBestActionContentRow>
              <NextBestActionTruncated ref={truncatedTextRef}>
                <Typography category="body" size="small" weight="medium" color="body">
                  {' '}
                  - {nextBestAction}
                </Typography>
              </NextBestActionTruncated>
              <ViewMoreLink
                type="button"
                $isVisible={hasEllipsis}
                onClick={handleViewMoreClick}
                aria-label="View full next best action"
              >
                View more
              </ViewMoreLink>
            </NextBestActionContentRow>
          </NextBestActionBlock>
        )}

        {nextBestAction && (
          <NextBestActionModal
            isOpen={isNextBestActionModalOpen}
            onClose={handleCloseNextBestActionModal}
            nextBestAction={nextBestAction}
            contactName={legalName}
          />
        )}

        {(activitySummary?.length > 0 || contact.email || contact.phone) && (
          <ActivitySectionRow>
            <ActivitySectionWrapper>
              <ActivityRow>
                {activitySummary?.map((item, index) => (
                  <React.Fragment key={index}>
                    {index > 0 && <ActivitySeparator>•</ActivitySeparator>}
                    <ActivityItem>
                      {item.icon && (
                        <Icon name={item.icon} width="14px" height="14px" />
                      )}
                      <Typography category="body" size="small" weight="medium" color="subtitle">
                        {item.text}
                      </Typography>
                    </ActivityItem>
                  </React.Fragment>
                ))}
                {contact.email && (
                  <>
                    {activitySummary?.length > 0 && <ActivitySeparator>•</ActivitySeparator>}
                    <CopyableContactItem iconName="email" value={contact.email} />
                  </>
                )}
                {contact.phone && (
                  <>
                    {(activitySummary?.length > 0 || contact.email) && <ActivitySeparator>•</ActivitySeparator>}
                    <CopyableContactItem iconName="call" value={contact.phone} />
                  </>
                )}
              </ActivityRow>
            </ActivitySectionWrapper>
          </ActivitySectionRow>
        )}
        </CardContentWrapper>
      </Card>
    </CardHighlightWrapper>
  );
};

export default ContactCard;
